import converter from 'number-to-words'
import strings from 'utils/strings'
import moment from 'moment'
import { isObject, isString, isNumber, isArray, toInt } from 'utils/types'
import { forbidExtraProps } from 'airbnb-prop-types'
import { hasPath, head, pathOr, prop, propSatisfies, __, includes } from 'ramda'

import {
  DATE_TIME_FORMAT,
  CTP,
  DATE_FORMAT,
  CEEAVehicleTabs,
} from 'utils/constants'

export const uriIt = (...args) => `/${args.join('/')}`

export const hasOwnPropInObj = (obj, key) =>
  Object.prototype.hasOwnProperty.call(obj, key)

export function isNumeric(value) {
  return !Number.isNaN(parseFloat(value)) && Number.isFinite(Number(value))
}

// is property value part of the array
export const propValueInArray = (propName, valuesArray) =>
  propSatisfies(includes(__, valuesArray), propName)

// null or undefined
export const isNullish = (thing) => thing == null // eslint-disable-line eqeqeq

export const isEmptyObject = (obj) =>
  Object.keys(obj).length === 0 && obj.constructor === Object

export const isEmptyString = (str) => !str || str === ''

/**
 * Truncates text to a maximum length with ellipsis
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncation (default: 40)
 * @returns {string} - Truncated text with ellipsis or original text if shorter
 */
export const truncateText = (text, maxLength = 40) =>
  text && text.length > maxLength
    ? `${text.slice(0, maxLength)}...`
    : text || ''

export function escapeRegExp(str = '') {
  return str.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&')
}

export function hexToDecimal(hex) {
  return parseInt(hex, 16)
}

// escape out odd characters from text
// ideal for the WYSIWYG editor so that things like ' and " don't glitch
export function escape(s) {
  return `${s}`
    .replace(/\\/g, '\\\\')
    .replace(/\t/g, '\\t')
    .replace(/\n/g, '\\n')
    .replace(/\u00A0/g, '\\u00A0')
    .replace(/&/g, '\\x26')
    .replace(/'/g, '\\x27')
    .replace(/"/g, '\\x22')
    .replace(/</g, '\\x3C')
    .replace(/>/g, '\\x3E')
  // if you add one here, add in unescape as well
}
// convert back the escaped chars
export function unescape(s) {
  s = `${s}`
    .replace(/\\x3E/g, '>')
    .replace(/\\x3C/g, '<')
    .replace(/\\x22/g, '"')
    .replace(/\\x27/g, "'")
    .replace(/\\x26/g, '&')
    .replace(/\\u00A0/g, '\u00A0')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')

  return s.replace(/\\\\/g, '\\')
}

// return serialNumber of vehicle and vehicle area(deafult summary).
// works only in vehicle context
export function getSerialNumberAndVehicleArea(location) {
  const pathName = prop('pathname', location)
  const serialNumber = pathName ? pathName.split('/')[2] : null
  const vehicleArea = pathName
    ? pathName.split('/')[3]
    : CEEAVehicleTabs.summary
  return {
    serialNumber,
    vehicleArea,
  }
}

export function splitName(fullName) {
  if (fullName == undefined) return {} // eslint-disable-line eqeqeq
  // Does not check for more/less than two names, but won't die with "".
  const names = fullName.split(' ')
  return {
    names,
    last: names[names.length - 1],
    first: names[0],
  }
}

export function camelCaseString(str) {
  if (isEmptyString(str)) return str
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, '')
}

export function capitalizeFirst(str) {
  if (isEmptyString(str)) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function capitalizeFirstOnly(str) {
  if (isEmptyString(str)) return str
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export function capitalizeEachWord(str) {
  return str
    .split(' ')
    .map((word) => capitalizeFirst(word))
    .join(' ')
}

export function capitalizeAll(str) {
  return str
    .split('')
    .map((l) => l.toUpperCase())
    .join('')
}

export function zeroPad(numStr, length) {
  const s = `000000000${numStr}`
  return s.slice(-length)
}

export function numberToWord(number) {
  if (isNumber(number)) {
    return converter.toWords(number)
  }

  throw Error('numberToWord error: Not a number')
}

export function titleCaseString(string) {
  if (!string || string === '') return ''
  if (typeof string !== 'string') return string
  return `${string.charAt(0).toUpperCase()}${string.substr(1)}`
}

const wrapArrayIndex = (array, idx) => {
  if (idx > array.length - 1) return 0
  if (idx < 0) return array.length - 1
  return idx
}
export const rotateArrayIdx = (array, idx, up) =>
  wrapArrayIndex(array, up ? idx + 1 : idx - 1)

export const enhanceProps = (propTypes) => forbidExtraProps(propTypes)

// returns a localized date/time string, given a UTC time string.
// format is optional, and corresponds to the format specs for moment.js.
// returns 'Invalid date' if utcTimeString cannot be parsed.
export const getLocalTime = (utcTimeString, format = DATE_TIME_FORMAT) =>
  /* when we are ready to replace moment, this will change the utcString into local time
   * const utcDate = new Date(utcTimeString)
   * const localDate = new Date(Date.UTC(utcDate.getFullYear(), utcDate.getMonth(), utcDate.getDate(),  utcDate.getHours(), utcDate.getMinutes(), utcDate.getSeconds()))
   */
  moment.utc(utcTimeString).local().format(format)

export const toDate = (date) => moment(date).utc().format(DATE_FORMAT)

export const getTimeAsIs = (utcTimeString, format = DATE_TIME_FORMAT) =>
  moment.utc(utcTimeString).format(format)

export const getDate = (trueDateString) => {
  // this function is for displaying DATE_TIME_FORMAT formats only e.g. fleet management page
  const date = new Date(trueDateString)
  const day = date.getDate()
  const month = date.toLocaleDateString('en-US', { month: 'short' }) // may cause issues on the first of the month/last of the month
  const year = date.getFullYear()
  return `${day} ${month} ${year}`
}

export function getElapsedTime(pastDate, longFormat) {
  const date = new Date(pastDate)
  const defaultTimeZone = 'en-US'
  const second = 1000
  const min = second * 60
  const hour = min * 60
  const day = hour * 24
  const week = day * 7
  const month = day * 30
  const year = day * 365
  const now = new Date()
  const elapsedTime = now.getTime() - date.getTime()
  const minutesElapsed = Math.round(elapsedTime / min)
  const daysElapsed = Math.round(elapsedTime / day)
  const hoursElapsed = Math.floor(elapsedTime / hour)
  const weeksElapsed = Math.round(elapsedTime / week)
  const monthsElapsed = Math.round(elapsedTime / month)
  const yearsElapsed = Math.round(elapsedTime / year)

  const formatTimeElapsed = (timeVal, unitVal) => {
    if (timeVal !== 0) {
      return { time: timeVal, unit: unitVal }
    }
    if (unitVal === strings.minute) {
      return { time: 0, unit: strings.minute }
    }

    return null
  }

  const formatZeros = (val) => (val.toString().length === 1 ? `0${val}` : val)

  const formatLocalTime = (timeStr) => {
    const timeStrArr = timeStr.split(':')
    return `${formatZeros(timeStrArr[0])}:${timeStrArr[1]}`
  }

  if (!longFormat) {
    return (
      formatTimeElapsed(yearsElapsed, strings.year) ||
      formatTimeElapsed(monthsElapsed, strings.month) ||
      formatTimeElapsed(weeksElapsed, strings.week) ||
      formatTimeElapsed(daysElapsed, strings.day) ||
      formatTimeElapsed(hoursElapsed, strings.hour) ||
      formatTimeElapsed(minutesElapsed, strings.minute)
    )
  }

  return [
    `${formatZeros(date.getDate())} ${date.toLocaleString(defaultTimeZone, {
      month: 'short',
    })} ${date.getFullYear()}`,
    formatLocalTime(
      date.toLocaleTimeString(defaultTimeZone, { hour12: false })
    ),
  ]
}

export const generateReferenceParamURI = (id, release) => {
  const ret = `/reference-parameters/${id}/${release}`
  return ret
}
export const generateReferenceParamURIFromRP = (rp) =>
  generateReferenceParamURI(rp.refParameterNumber, rp.release)

export const convertQueryStringToObj = (str) =>
  str
    .replace(/(^\?)/, '')
    .split('&')
    .reduce((acc, item) => {
      const [key, value] = item.split('=')
      const val = value.includes(',') ? value.split(',') : value
      return {
        ...acc,
        [key]: val,
      }
    }, {})
/* remove empty, undefined or null values from an object MB 06.12.18 */
export const cleanObject = (obj) => {
  if (!obj) {
    return {}
  }
  Object.keys(obj).forEach((key) => {
    if (obj[key] === '' || obj[key] === undefined || obj[key] === null) {
      delete obj[key]
    }
  })
  return obj
}

// Closure to ensure these methods dont get modified by anything, can move to utils in future
;(() => {
  /**
   * Decimal adjustment of a number.
   * Borrowed from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/round
   *
   * @param {String}  type  The type of adjustment.
   * @param {Number}  value The number.
   * @param {Integer} exp   The exponent (the 10 logarithm of the adjustment base).
   * @returns {Number} The adjusted value.
   */
  function decimalAdjust(type, value, exp) {
    // If the exp is undefined or zero...
    if (typeof exp === 'undefined' || +exp === 0 || Number.isNaN(exp)) {
      return Math[type](value)
    }
    value = +value
    exp = +exp
    // If the value is not a number or the exp is not an integer...
    if (
      value === null ||
      Number.isNaN(value) ||
      !(typeof exp === 'number' && exp % 1 === 0)
    ) {
      return NaN
    }
    // If the value is negative...
    if (value < 0) {
      return -decimalAdjust(type, -value, exp)
    }
    // Shift
    value = value.toString().split('e')
    value = Math[type](+`${value[0]}e${value[1] ? +value[1] - exp : -exp}`)
    // Shift back
    value = value.toString().split('e')
    return +`${value[0]}e${value[1] ? +value[1] + exp : exp}`
  }

  // Decimal round
  if (!Math.round10) {
    Math.round10 = (value, exp) => decimalAdjust('round', value, exp)
  }
  // Decimal floor
  if (!Math.floor10) {
    Math.floor10 = (value, exp) => decimalAdjust('floor', value, exp)
  }
  // Decimal ceil
  if (!Math.ceil10) {
    Math.ceil10 = (value, exp) => decimalAdjust('ceil', value, exp)
  }
})()

export const composeHasThingBeenEdited = (ignoreKeys = []) => {
  const hasThingBeenEdited = (edited, original) => {
    if (edited && !original) {
      return true
    }
    if (!edited && original) {
      return true
    }
    if (isNumber(original)) {
      if (edited !== original) return true
    } else if (isString(original)) {
      if (edited.length !== original.length || edited !== original) return true
    } else if (isArray(original)) {
      if (edited.length !== original.length) return true
      const arrayValNotEqual = original.some((originalItem, index) =>
        hasThingBeenEdited(edited[index], originalItem)
      )
      if (arrayValNotEqual) return true
    } else if (isObject(original)) {
      // the duplicate SP redux flow adds a lot of boilerplate properties with null values that
      // may not be necessary for the "edited" SP once it gets saved. So we just need to inspect
      // values from the original.
      const valuesAreNotEqual = Object.keys(original)
        .filter((key) => !ignoreKeys.includes(key))
        .some((key) => hasThingBeenEdited(edited[key], original[key]))
      if (valuesAreNotEqual) return true
    }
    return false
  }

  return hasThingBeenEdited
}

export const classNames = (...props) => {
  if (!props) return null

  const classValidator = (val) => isString(val) || isNumber(val)

  return props
    .reduce((acc, current) => {
      if (classValidator(current)) {
        acc.push(current)
        return acc
      }
      if (isObject(current)) {
        const objClasses = Object.keys(current).reduce((objAcc, key) => {
          const value = current[key]

          if (value && classValidator(key)) {
            objAcc.push(key.toString())
          }

          return objAcc
        }, [])

        return [...acc, ...objClasses]
      }
      return acc
    }, [])
    .join(' ')
}

export const parseCrumbPath = (path) => {
  const resource = path
    .split('/')[1]
    .split('-')
    .reduce((accum, str) => `${accum}${` ${titleCaseString(str)}`}`)

  if (resource === CTP) return `${resource.toUpperCase()} ${strings.search}`
  if (resource === 'pto Templates') return 'PTO Template Search'
  return `${titleCaseString(resource)} ${strings.search}`
}

export const maintainLocState = (uri, locationObj) => ({
  pathname: uri,
  state: {
    ...(locationObj && locationObj.state),
  },
})
export const sortListByKey = (key) => (a, b) => a[key] < b[key] ? 1 : -1

// "1.0.0.0 0.42.0" => 42
export const getMinorVersion = (version) =>
  toInt(head(version.split(' ').pop().split('.').splice(1, 1)))

// "1.0.0.0 0.42.1" => 1
export const getPreMinorVersion = (version) =>
  toInt(head(version.split(' ').pop().split('.').slice(2, 1)))

// "1.0.0.0 0.42.1-7" => 1
export const getPreMinorVersionWithBeta = (version) =>
  toInt(getPreMinorVersion(version).split('-').shift())

export const handlePreVersusBeta = (version) => {
  try {
    const hasBeta = version.split(' ').pop().includes('-')

    if (hasBeta) return getPreMinorVersionWithBeta(version)

    return getPreMinorVersion(version)
  } catch (e) {
    console.warn(e)
    return 0
  }
}

export const CURRENT_DATE = moment().utc().format(DATE_FORMAT)

/**
 * To check if pathname contains "summary"
 * @param {string|History} data
 * @returns Boolean
 */
export const isSummaryPath = (data) => {
  let pathname = data

  if (isObject(data) && hasPath(['location', 'pathname'], data)) {
    pathname = pathOr('', ['location', 'pathname'], data)
  }

  if (isString(pathname)) return pathname.includes('summary')

  return false
}

/**
 * To if pathname contains X
 * @param {string|History} data
 * @param {string} match
 * @returns Boolean
 */
export const doesPathContain = (data, match) => {
  let pathname = data

  if (isObject(data) && hasPath(['location', 'pathname'], data)) {
    pathname = pathOr('', ['location', 'pathname'], data)
  }

  if (isString(pathname)) return pathname.includes(match)

  return false
}

export const buildQueryString = (list, param, collectionName) => {
  if (!list || !param || !collectionName) return ''

  return list.reduce((previousValue, currentValue, index) => {
    const value = currentValue.replace(`/${collectionName}/`, '')
    const delimiter = index === 0 ? '' : '&'
    return `${previousValue}${delimiter}${param}=${value}`
  }, '')
}
